import { supabase } from '../config/supabase';
import { User, UserRole } from '../types';

export const usersService = {
  async getAll(): Promise<User[]> {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .order('email');

    if (error) {
      console.error('Error getting users:', error);
      throw new Error('Impossible de récupérer la liste des utilisateurs');
    }

    return (data || []).map(user => ({
      id: user.id,
      email: user.email,
      role: user.role as UserRole,
      organizationId: user.organization_id,
      displayName: user.display_name,
      avatarUrl: user.avatar_url,
      lastLoginAt: user.last_login_at ? new Date(user.last_login_at) : undefined,
      createdAt: user.created_at ? new Date(user.created_at) : new Date()
    }));
  },

  async getById(id: string): Promise<User | null> {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw new Error('Impossible de récupérer l\'utilisateur');
    }

    if (!data) return null;

    return {
      id: data.id,
      email: data.email,
      role: data.role as UserRole,
      organizationId: data.organization_id,
      displayName: data.display_name,
      avatarUrl: data.avatar_url,
      lastLoginAt: data.last_login_at ? new Date(data.last_login_at) : undefined,
      createdAt: data.created_at ? new Date(data.created_at) : new Date()
    };
  },

  async create(email: string, password: string, role: UserRole, organizationId?: string): Promise<User> {
    // Create user via Supabase Auth
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password
    });

    if (authError) {
      console.error('Error creating user:', authError);
      throw new Error(authError.message);
    }

    if (!authData.user) {
      throw new Error('Impossible de créer l\'utilisateur');
    }

    // Create or update profile in users table (upsert to avoid race condition with onAuthStateChange)
    const { error: profileError } = await supabase.from('users').upsert({
      id: authData.user.id,
      email: authData.user.email,
      role,
      organization_id: organizationId || null,
      created_at: new Date().toISOString()
    }, { onConflict: 'id' });

    if (profileError) {
      console.error('Error creating user profile:', profileError);
      throw new Error('Impossible de créer le profil utilisateur');
    }

    return {
      id: authData.user.id,
      email: authData.user.email || '',
      role,
      organizationId: organizationId || null,
      createdAt: new Date()
    };
  },

  async update(id: string, data: Partial<User>): Promise<void> {
    const updateData: Record<string, unknown> = {};
    if (data.role) updateData.role = data.role;
    if (data.displayName !== undefined) updateData.display_name = data.displayName;
    if (data.organizationId !== undefined) updateData.organization_id = data.organizationId;
    if (data.avatarUrl !== undefined) updateData.avatar_url = data.avatarUrl;

    const { error } = await supabase
      .from('users')
      .update(updateData)
      .eq('id', id);

    if (error) {
      console.error('Error updating user:', error);
      throw new Error('Impossible de mettre à jour l\'utilisateur');
    }
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('users')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting user:', error);
      throw new Error('Impossible de supprimer l\'utilisateur');
    }
  }
};
